import {
  init, miniApp, initDataRaw, locationManager, requestContactComplete, restoreInitData,
  retrieveLaunchParams,
} from '@telegram-apps/sdk-react';

let initialized = false;

/**
 * Initialize the Telegram Mini App SDK and signal `ready` so the platform
 * hides the loading placeholder. Safe to call multiple times (idempotent).
 *
 * Outside the Telegram client (plain browser dev) the SDK throws on
 * retrieveLaunchParams — we swallow that so devs can still load the app
 * with VITE_DEV_TELEGRAM_ID instead.
 */
export function initTelegram(): { isTelegram: boolean } {
  if (initialized) return { isTelegram: true };
  try {
    retrieveLaunchParams();
  } catch {
    initialized = true;
    return { isTelegram: false };
  }

  init();
  // `initDataRaw` is an empty signal until this runs — without it getInitDataRaw() returns
  // undefined inside Telegram, no X-Tg-Init-Data header is sent, and every API call 401s.
  restoreInitData();
  if (miniApp.ready.isAvailable()) {
    miniApp.ready();
  }
  initialized = true;
  return { isTelegram: true };
}

/**
 * Returns the raw initData string that the backend expects in the
 * `X-Tg-Init-Data` header. `undefined` outside Telegram — caller should
 * fall back to the dev header in that case.
 */
export function getInitDataRaw(): string | undefined {
  try {
    return initDataRaw();
  } catch {
    return undefined;
  }
}

/**
 * Asks Telegram for the user's own phone number instead of making them type it. Returns the
 * raw signed payload for the backend to verify, or `undefined` when the platform is too old
 * (requestContact landed in Mini Apps 6.9) — callers keep the manual field as the fallback.
 * Throws if the user declines the prompt.
 */
export async function requestPhoneContact(): Promise<string | undefined> {
  if (!requestContactComplete.isAvailable()) return undefined;
  const { raw } = await requestContactComplete();
  return raw;
}

/**
 * Asks Telegram for the courier's current position, so the city can be filled in instead of
 * typed. Returns `undefined` when the platform has no location manager (it landed in Mini Apps
 * 8.0) — callers keep the text field as the fallback. Throws if the user declines.
 *
 * Screenshots carry no GPS of their own — there is no camera in a screenshot — so this is the
 * only place a real position can come from.
 */
export async function requestUserLocation(): Promise<{ lat: number; lon: number } | undefined> {
  if (!locationManager.mount.isAvailable() || !locationManager.requestLocation.isAvailable()) {
    return undefined;
  }
  if (!locationManager.isMounted()) {
    await locationManager.mount();
  }
  const l = await locationManager.requestLocation();
  return { lat: l.latitude, lon: l.longitude };
}

interface TelegramPopups {
  showAlert?: (message: string, cb?: () => void) => void;
  showConfirm?: (message: string, cb?: (ok: boolean) => void) => void;
  isVersionAtLeast?: (v: string) => boolean;
}

/**
 * telegram-web-app.js is loaded unconditionally from index.html, so `Telegram.WebApp` and its
 * popup methods exist even in a plain browser — but outside a real client the reported Bot API
 * version is 6.0, where showAlert/showConfirm are no-ops that never invoke their callback.
 * Calling them there logs "Method showPopup is not supported" and leaves the promise pending
 * forever. Popups landed in Bot API 6.2, so gate on that.
 */
function popups(): TelegramPopups | undefined {
  const wa = (window as unknown as { Telegram?: { WebApp?: TelegramPopups } }).Telegram?.WebApp;
  return wa?.isVersionAtLeast?.('6.2') ? wa : undefined;
}

/**
 * Telegram's in-app WebView suppresses the browser's own `alert`/`confirm` on several
 * platforms, so a native dialog can silently never appear. Use the platform popups when
 * we're inside Telegram and fall back to the browser ones for plain-browser dev.
 */
export function showAlert(message: string): Promise<void> {
  const p = popups();
  if (p?.showAlert) return new Promise((resolve) => p.showAlert!(message, () => resolve()));
  alert(message);
  return Promise.resolve();
}

export function showConfirm(message: string): Promise<boolean> {
  const p = popups();
  if (p?.showConfirm) return new Promise((resolve) => p.showConfirm!(message, (ok) => resolve(ok)));
  return Promise.resolve(confirm(message));
}

/** Closes the Mini App when running inside Telegram; no-op in a plain browser. */
export function closeMiniApp(): void {
  const wa = (window as unknown as { Telegram?: { WebApp?: { close?: () => void } } }).Telegram?.WebApp;
  wa?.close?.();
}
